import { useState } from 'react'

import { Box, Icon, Tag, Text, Touchable } from '@factorialco/deprecated-design-system'
import {
  BudgetFragment,
  FinanceValueObjectsBudgetPeriodicityEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { budgetStatusText, budgetTagColor } from 'modules/finance/lib/budgetTagInfo'
import { formatPeriod } from 'modules/finance/lib/budgets'

const getPeriod = (periodicity: FinanceValueObjectsBudgetPeriodicityEnum) => {
  switch (periodicity) {
    case 'daily':
      return 'day'
    case 'weekly':
      return 'week'
    case 'monthly':
      return 'month'
    default:
      return 'year'
  }
}

interface Props {
  budget: BudgetFragment
  onPeriodChange: (start: string, end: string) => void
}

const PeriodNavigation = ({ budget, onPeriodChange }: Props) => {
  const period = getPeriod(budget.periodicity)

  const periodStart = moment(budget.currentPeriodStart)
  const effectiveFrom = moment(budget.effectiveFrom).startOf(period)

  const [currentDate, setCurrentDate] = useState(periodStart)

  const previousDate = currentDate.clone().subtract(1, `${period}s`)
  const nextDate = currentDate.clone().add(1, `${period}s`)

  const showPrevious = previousDate >= effectiveFrom
  const showNext = nextDate <= periodStart

  const setDateForward = () => {
    const startDate = dateToString(nextDate, 'backend_datetime')
    const endDate = dateToString(nextDate.clone().endOf(period), 'backend_datetime')

    onPeriodChange(startDate, endDate)
    setCurrentDate(nextDate)
  }

  const setDateBackward = () => {
    const startDate = dateToString(previousDate, 'backend_datetime')
    const endDate = dateToString(previousDate.clone().endOf(period), 'backend_datetime')

    onPeriodChange(startDate, endDate)
    setCurrentDate(previousDate)
  }

  return (
    <Box width="full" flexDirection="row" paddingTop="s16" paddingBottom="s4">
      <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="center" flexGrow>
        {showPrevious && (
          <Touchable
            onClick={() => setDateBackward()}
            aria-label={i18n.t('budgets.detail.period_navigation.previous')}
          >
            <Icon.Small icon="chevron-left" />
          </Touchable>
        )}
        <Text.Normal weight="medium" color="grey1000" whiteSpace="nowrap">
          {formatPeriod(currentDate, budget.periodicity)}
        </Text.Normal>
        {showNext && (
          <Touchable
            onClick={() => setDateForward()}
            aria-label={i18n.t('budgets.detail.period_navigation.next')}
          >
            <Icon.Small icon="chevron-right" />
          </Touchable>
        )}
      </Box>

      <Box width="fit-content">
        <Tag leftItem="circle" showCircle type={budgetTagColor(budget.periodStatus)}>
          {budgetStatusText(budget.periodStatus)}
        </Tag>
      </Box>
    </Box>
  )
}

export default PeriodNavigation
